import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { teamService } from '../../../services/team/TeamService';
import type { TeamMember } from '../../../types/team';
import { dmConversationId, peerIdFromDmConversation } from '../utils/conversationId';
import { sanitizeChatPlainText } from '../utils/sanitize';
import { readFileAsDataUrl, validateTeamChatFile } from '../utils/fileValidation';
import { virusScanPlaceholder } from '../utils/virusScanPlaceholder';
import { chatMessagePreview, canEditOrDeleteOwnMessage } from '../utils/messagePolicy';
import { isTeamChatAdminRole } from '../utils/teamChatRoles';
import { loadChatBlob, saveChatBlob, type PersistedChatBlob } from '../services/teamChatPersistence';
import { getTeamChatTransport } from '../socket/teamChatTransport';
import type {
  ChatAttachment,
  ChatAvailability,
  ChatMessage,
  ChatTeammateRow,
  FloatingPeerWindow,
  TeamChatWirePayload,
  WindowGeom,
} from '../types';
import { toast } from 'sonner@2.0.3';

const DEFAULT_GEOM = (): WindowGeom => ({
  x: 120,
  y: 120,
  w: 380,
  h: 440,
  minimized: false,
  maximized: false,
  zIndex: 50,
});

function formatRelative(ts: number): string {
  const s = Math.floor((Date.now() - ts) / 1000);
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

function inferAvailability(m: TeamMember, self: boolean): ChatAvailability {
  if (self) return 'available';
  if (m.status === 'blocked') return 'offline';
  if (!m.lastActivity) return 'away';
  const diff = Date.now() - new Date(m.lastActivity).getTime();
  if (diff < 10 * 60 * 1000) return 'available';
  if (diff < 60 * 60 * 1000) return 'away';
  return 'offline';
}

function lastActiveLabel(m: TeamMember): string {
  if (!m.lastActivity) return 'No recent activity';
  return formatRelative(new Date(m.lastActivity).getTime());
}

interface TeamChatContextValue {
  tenantKey: string;
  teammates: ChatTeammateRow[];
  refreshDirectory: () => Promise<void>;
  blob: PersistedChatBlob;
  unreadTotal: number;
  floating: FloatingPeerWindow[];
  pageSelectedPeerId: string | null;
  setPageSelectedPeerId: (id: string | null) => void;
  openFloatingChat: (peerId: string) => void;
  closeFloatingChat: (peerId: string) => void;
  focusFloating: (peerId: string) => void;
  setWindowGeom: (peerId: string, geom: Partial<WindowGeom>) => void;
  sendDm: (peerId: string, body: string, files?: File[]) => Promise<void>;
  editOwnDmMessage: (peerId: string, messageId: string, bodyRaw: string) => void;
  softDeleteOwnDmMessage: (peerId: string, messageId: string) => void;
  /** Tenant admins may expand deletion audit (original content) for compliance. */
  canViewDeletionAudit: boolean;
  notifyTyping: (peerId: string) => void;
  typingPeerIds: Set<string>;
  markConversationRead: (peerId: string) => void;
  getMessages: (peerId: string) => ChatMessage[];
  togglePinConversation: (peerId: string) => void;
  toggleFavoriteConversation: (peerId: string) => void;
  toggleStarMessage: (messageId: string) => void;
  soundEnabled: boolean;
  setSoundEnabled: (v: boolean) => void;
  /** Minimize floating window into bottom dock (Messenger-style). */
  dockFloatingChat: (peerId: string) => void;
  restoreDockedChat: (peerId: string) => void;
}

const TeamChatContext = createContext<TeamChatContextValue | null>(null);

export function useTeamChat(): TeamChatContextValue {
  const ctx = useContext(TeamChatContext);
  if (!ctx) throw new Error('useTeamChat must be used within TeamChatProvider');
  return ctx;
}

export function TeamChatProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const tenantKey = (user?.email || 'user').toLowerCase();
  const userId = user?.id || 'me';

  const [blob, setBlob] = useState<PersistedChatBlob>(() => loadChatBlob(tenantKey));
  const [directoryTick, setDirectoryTick] = useState(0);
  const [floating, setFloating] = useState<FloatingPeerWindow[]>([]);
  const [pageSelectedPeerId, setPageSelectedPeerId] = useState<string | null>(null);
  const [typingPeerIds, setTypingPeerIds] = useState<Set<string>>(new Set());
  const [soundEnabled, setSoundEnabledState] = useState(() => localStorage.getItem('coremonit_chat_sound') !== 'false');
  const zCounter = useRef(60);
  const floatingPeerIdsRef = useRef<Set<string>>(new Set());

  const typingClearTimers = useRef<Record<string, number>>({});

  useEffect(() => {
    floatingPeerIdsRef.current = new Set(floating.map((f) => f.peerId));
  }, [floating]);

  const transport = useMemo(() => getTeamChatTransport(tenantKey), [tenantKey]);

  useEffect(() => {
    setBlob(loadChatBlob(tenantKey));
  }, [tenantKey]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await teamService.syncFromBackend();
        if (!cancelled) setDirectoryTick((n) => n + 1);
      } catch {
        if (!cancelled) setDirectoryTick((n) => n + 1);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantKey]);

  const persist = useCallback(
    (next: PersistedChatBlob) => {
      setBlob(next);
      saveChatBlob(tenantKey, next);
    },
    [tenantKey],
  );

  const members = useMemo(() => {
    void directoryTick;
    return teamService.getMembers();
  }, [directoryTick]);

  const teammates: ChatTeammateRow[] = useMemo(() => {
    const selfMember: TeamMember | null = user
      ? ({
          id: userId,
          employeeId: 'self',
          firstName: user.name?.split(' ')[0] || 'You',
          lastName: user.name?.split(' ').slice(1).join(' ') || '',
          email: user.email,
          department: '—',
          jobTitle: user.role || 'Member',
          roleId: 'self',
          roleName: user.role || 'Member',
          accessType: 'permanent',
          status: 'active',
          requirePasswordReset: false,
          require2FA: false,
          loginCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: 'system',
          invitationSent: false,
          invitationAccepted: true,
        } as TeamMember)
      : null;

    const simplified: ChatTeammateRow[] = [];
    if (selfMember) {
      const convId = dmConversationId(userId, selfMember.id);
      const list = blob.messagesByConversation[convId] || [];
      const last = list[list.length - 1];
      simplified.push({
        member: selfMember,
        isSelf: true,
        availability: 'available',
        lastActiveLabel: 'You',
        unread: 0,
        lastPreview: last ? chatMessagePreview(last) : undefined,
        lastAt: last?.createdAt,
      });
    }

    for (const m of members) {
      if (m.id === userId) continue;
      const convId = dmConversationId(userId, m.id);
      const list = blob.messagesByConversation[convId] || [];
      const last = list[list.length - 1];
      const lastReadRow = blob.readReceipts[convId]?.[userId];
      let unread = 0;
      if (lastReadRow) {
        const idx = list.findIndex((x) => x.id === lastReadRow);
        const tail = idx >= 0 ? list.slice(idx + 1) : list;
        unread = tail.filter((x) => x.authorId !== userId).length;
      } else {
        unread = list.filter((x) => x.authorId !== userId).length;
      }
      simplified.push({
        member: m,
        isSelf: false,
        availability: inferAvailability(m, false),
        lastActiveLabel: lastActiveLabel(m),
        unread,
        lastPreview: last ? chatMessagePreview(last) : undefined,
        lastAt: last?.createdAt,
      });
    }

    return simplified.sort((a, b) => {
      if (a.isSelf) return -1;
      if (b.isSelf) return 1;
      return (b.lastAt || 0) - (a.lastAt || 0);
    });
  }, [blob, members, user?.email, user?.id, user?.name, user?.role, userId]);

  const unreadTotal = useMemo(
    () => teammates.filter((t) => !t.isSelf).reduce((s, t) => s + t.unread, 0),
    [teammates],
  );

  const refreshDirectory = useCallback(async () => {
    try {
      await teamService.syncFromBackend();
    } finally {
      setDirectoryTick((n) => n + 1);
    }
  }, []);

  const markConversationRead = useCallback(
    (peerId: string) => {
      const convId = dmConversationId(userId, peerId);
      const list = blob.messagesByConversation[convId] || [];
      const lastPeer = [...list].reverse().find((m) => m.authorId !== userId);
      if (!lastPeer) return;
      const next: PersistedChatBlob = {
        ...blob,
        readReceipts: {
          ...blob.readReceipts,
          [convId]: {
            ...(blob.readReceipts[convId] || {}),
            [userId]: lastPeer.id,
          },
        },
      };
      persist(next);
      transport.publish({
        type: 'message_read',
        conversationId: convId,
        fromUserId: userId,
        readUpToMessageId: lastPeer.id,
      });
    },
    [blob, persist, transport, userId],
  );

  const openFloatingChat = useCallback(
    (peerId: string) => {
      if (peerId === userId) {
        toast.message('Notes to self', { description: 'Open Team → Chat for your saved thread (demo).' });
        return;
      }
      markConversationRead(peerId);
      setFloating((prev) => {
        const exists = prev.find((p) => p.peerId === peerId);
        zCounter.current += 1;
        const z = zCounter.current;
        if (exists) {
          return prev.map((p) =>
            p.peerId === peerId
              ? {
                  ...p,
                  presentation: 'floating',
                  geom: { ...p.geom, zIndex: z, minimized: false },
                }
              : p,
          );
        }
        const offset = prev.filter((w) => w.presentation !== 'docked').length * 24;
        const geom = { ...DEFAULT_GEOM(), x: DEFAULT_GEOM().x + offset, y: DEFAULT_GEOM().y + offset, zIndex: z };
        return [...prev, { peerId, geom, presentation: 'floating' as const }];
      });
    },
    [markConversationRead, userId],
  );

  const closeFloatingChat = useCallback((peerId: string) => {
    setFloating((prev) => prev.filter((p) => p.peerId !== peerId));
  }, []);

  const focusFloating = useCallback((peerId: string) => {
    zCounter.current += 1;
    const z = zCounter.current;
    setFloating((prev) =>
      prev.map((p) => (p.peerId === peerId ? { ...p, geom: { ...p.geom, zIndex: z } } : p)),
    );
  }, []);

  const setWindowGeom = useCallback((peerId: string, geom: Partial<WindowGeom>) => {
    setFloating((prev) =>
      prev.map((p) => (p.peerId === peerId ? { ...p, geom: { ...p.geom, ...geom } } : p)),
    );
  }, []);

  const playSoftPing = useCallback(() => {
    if (!soundEnabled) return;
    try {
      const Ctx = window.AudioContext || (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g);
      g.connect(ctx.destination);
      o.frequency.value = 880;
      g.gain.value = 0.04;
      o.start();
      o.stop(ctx.currentTime + 0.06);
      ctx.resume?.();
    } catch {
      /* ignore */
    }
  }, [soundEnabled]);

  const setSoundEnabled = useCallback((v: boolean) => {
    setSoundEnabledState(v);
    localStorage.setItem('coremonit_chat_sound', v ? 'true' : 'false');
  }, []);

  const appendMessages = useCallback(
    (convId: string, msg: ChatMessage) => {
      setBlob((prev) => {
        const list = [...(prev.messagesByConversation[convId] || [])];
        if (list.some((x) => x.id === msg.id)) return prev;
        list.push(msg);
        const next: PersistedChatBlob = {
          ...prev,
          messagesByConversation: { ...prev.messagesByConversation, [convId]: list },
        };
        saveChatBlob(tenantKey, next);
        return next;
      });
    },
    [tenantKey],
  );

  const upsertMessage = useCallback(
    (incoming: ChatMessage) => {
      const convId = incoming.conversationId;
      setBlob((prev) => {
        const list = [...(prev.messagesByConversation[convId] || [])];
        const idx = list.findIndex((x) => x.id === incoming.id);
        const existing = idx >= 0 ? list[idx] : null;
        let merged: ChatMessage;
        if (
          incoming.deletedAt &&
          existing &&
          !incoming.deletionAudit &&
          existing.deletionAudit &&
          existing.id === incoming.id
        ) {
          merged = { ...incoming, deletionAudit: existing.deletionAudit };
        } else if (existing) {
          merged = { ...existing, ...incoming };
        } else {
          merged = incoming;
        }
        if (idx === -1) list.push(merged);
        else list[idx] = merged;
        const next: PersistedChatBlob = {
          ...prev,
          messagesByConversation: { ...prev.messagesByConversation, [convId]: list },
        };
        saveChatBlob(tenantKey, next);
        return next;
      });
    },
    [tenantKey],
  );

  const canViewDeletionAudit = useMemo(() => isTeamChatAdminRole(user?.role), [user?.role]);

  const mergeReceipt = useCallback(
    (convId: string, readerId: string, msgId: string) => {
      setBlob((prev) => {
        const next: PersistedChatBlob = {
          ...prev,
          readReceipts: {
            ...prev.readReceipts,
            [convId]: {
              ...(prev.readReceipts[convId] || {}),
              [readerId]: msgId,
            },
          },
        };
        saveChatBlob(tenantKey, next);
        return next;
      });
    },
    [tenantKey],
  );

  useEffect(() => {
    const unsub = transport.subscribe((p: TeamChatWirePayload) => {
      if (p.type === 'ping' || p.type === 'pong') return;
      if (p.type === 'user_typing' && p.fromUserId && p.conversationId) {
        const peer = p.fromUserId;
        if (peer === userId) return;
        if (p.typing) {
          setTypingPeerIds((prev) => new Set(prev).add(peer));
          if (typingClearTimers.current[peer]) window.clearTimeout(typingClearTimers.current[peer]);
          typingClearTimers.current[peer] = window.setTimeout(() => {
            setTypingPeerIds((prev) => {
              const n = new Set(prev);
              n.delete(peer);
              return n;
            });
          }, 2500);
        }
        return;
      }
      if (p.type === 'message_read' && p.conversationId && p.fromUserId && p.readUpToMessageId) {
        mergeReceipt(p.conversationId, p.fromUserId, p.readUpToMessageId);
        return;
      }
      if ((p.type === 'message_sent' || p.type === 'message_received') && p.message) {
        const m = p.message;
        appendMessages(m.conversationId, { ...m, delivery: m.authorId === userId ? 'sent' : 'sent' });
        if (m.authorId !== userId) {
          const peer = peerIdFromDmConversation(m.conversationId, userId);
          const windowOpen = peer ? floatingPeerIdsRef.current.has(peer) : false;
          const tabHidden = document.visibilityState === 'hidden';
          if (!windowOpen || tabHidden) {
            playSoftPing();
            const preview = chatMessagePreview(m);
            toast.message('New chat message', { description: (preview || ' ').slice(0, 120) });
          }
        }
        return;
      }
      if ((p.type === 'message_updated' || p.type === 'message_deleted') && p.message) {
        upsertMessage(p.message);
        return;
      }
    });
    return () => unsub();
  }, [appendMessages, mergeReceipt, playSoftPing, transport, upsertMessage, userId]);

  useEffect(() => {
    transport.publish({ type: 'user_online', fromUserId: userId });
    return () => {
      transport.publish({ type: 'user_offline', fromUserId: userId });
    };
  }, [transport, userId]);

  const notifyTyping = useCallback(
    (peerId: string) => {
      const convId = dmConversationId(userId, peerId);
      transport.publish({ type: 'user_typing', conversationId: convId, fromUserId: userId, typing: true });
    },
    [transport, userId],
  );

  const editOwnDmMessage = useCallback(
    (peerId: string, messageId: string, bodyRaw: string) => {
      const body = sanitizeChatPlainText(bodyRaw);
      const convId = dmConversationId(userId, peerId);
      let updated: ChatMessage | null = null;
      setBlob((prev) => {
        const list = [...(prev.messagesByConversation[convId] || [])];
        const idx = list.findIndex((x) => x.id === messageId);
        if (idx === -1) return prev;
        const cur = list[idx];
        if (!canEditOrDeleteOwnMessage(cur, userId)) {
          toast.error('This message can no longer be edited.');
          return prev;
        }
        if (cur.authorId !== userId) return prev;
        if (!body.trim() && !(cur.attachments?.length)) {
          toast.error('Message cannot be empty.');
          return prev;
        }
        const nextMsg: ChatMessage = { ...cur, body, editedAt: Date.now() };
        list[idx] = nextMsg;
        updated = nextMsg;
        const next: PersistedChatBlob = {
          ...prev,
          messagesByConversation: { ...prev.messagesByConversation, [convId]: list },
        };
        saveChatBlob(tenantKey, next);
        return next;
      });
      if (updated) transport.publish({ type: 'message_updated', conversationId: convId, message: updated });
    },
    [tenantKey, transport, userId],
  );

  const softDeleteOwnDmMessage = useCallback(
    (peerId: string, messageId: string) => {
      const convId = dmConversationId(userId, peerId);
      let wire: ChatMessage | null = null;
      setBlob((prev) => {
        const list = [...(prev.messagesByConversation[convId] || [])];
        const idx = list.findIndex((x) => x.id === messageId);
        if (idx === -1) return prev;
        const cur = list[idx];
        if (!canEditOrDeleteOwnMessage(cur, userId)) {
          toast.error('This message can no longer be deleted.');
          return prev;
        }
        if (cur.authorId !== userId) return prev;
        const deletedAt = Date.now();
        const full: ChatMessage = {
          ...cur,
          deletionAudit: {
            originalBody: cur.body,
            originalAttachments: cur.attachments ? cur.attachments.map((a) => ({ ...a })) : undefined,
            deletedAt,
            deletedBy: userId,
            editedSnapshotBeforeDelete: cur.editedAt ? { body: cur.body, editedAt: cur.editedAt } : undefined,
          },
          body: '',
          attachments: undefined,
          deletedAt,
          deletedBy: userId,
        };
        list[idx] = full;
        wire = {
          id: full.id,
          conversationId: full.conversationId,
          authorId: full.authorId,
          createdAt: full.createdAt,
          body: '',
          attachments: undefined,
          deletedAt: full.deletedAt,
          deletedBy: full.deletedBy,
          editedAt: full.editedAt,
          delivery: full.delivery,
        };
        const next: PersistedChatBlob = {
          ...prev,
          messagesByConversation: { ...prev.messagesByConversation, [convId]: list },
        };
        saveChatBlob(tenantKey, next);
        return next;
      });
      if (wire) transport.publish({ type: 'message_deleted', conversationId: convId, message: wire });
    },
    [tenantKey, transport, userId],
  );

  const sendDm = useCallback(
    async (peerId: string, bodyRaw: string, files?: File[]) => {
      const body = sanitizeChatPlainText(bodyRaw);
      if (!body && (!files || files.length === 0)) return;
      const convId = dmConversationId(userId, peerId);
      const attachments: ChatAttachment[] = [];
      if (files?.length) {
        for (const f of files) {
          const ok = validateTeamChatFile(f);
          if (ok.ok !== true) {
            toast.error(ok.reason);
            continue;
          }
          const scan = await virusScanPlaceholder(f);
          if (scan.ok !== true) {
            toast.error(scan.reason);
            continue;
          }
          try {
            const dataUrl = await readFileAsDataUrl(f);
            attachments.push({
              id: `att_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
              name: f.name,
              mime: f.type,
              size: f.size,
              dataUrl,
            });
          } catch {
            toast.error('Could not read file');
          }
        }
      }
      const msg: ChatMessage = {
        id: `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`,
        conversationId: convId,
        authorId: userId,
        body,
        createdAt: Date.now(),
        attachments: attachments.length ? attachments : undefined,
        delivery: 'sending',
      };
      appendMessages(convId, msg);
      transport.publish({ type: 'message_sent', message: msg });
      if (attachments.length) {
        transport.publish({
          type: 'file_uploaded',
          conversationId: convId,
          fromUserId: userId,
          attachmentMeta: attachments.map((a) => ({ id: a.id, name: a.name, mime: a.mime, size: a.size }))[0],
        });
      }
      window.setTimeout(() => {
        setBlob((prev) => {
          const list = [...(prev.messagesByConversation[convId] || [])];
          const idx = list.findIndex((x) => x.id === msg.id);
          if (idx === -1) return prev;
          list[idx] = { ...list[idx], delivery: 'sent' };
          const next = { ...prev, messagesByConversation: { ...prev.messagesByConversation, [convId]: list } };
          saveChatBlob(tenantKey, next);
          return next;
        });
      }, 120);
    },
    [appendMessages, tenantKey, transport, userId],
  );

  const getMessages = useCallback(
    (peerId: string) => {
      const convId = dmConversationId(userId, peerId);
      return blob.messagesByConversation[convId] || [];
    },
    [blob.messagesByConversation, userId],
  );

  const togglePinConversation = useCallback((peerId: string) => {
    const convId = dmConversationId(userId, peerId);
    setBlob((prev) => {
      const pins = new Set(prev.pinnedConversationIds);
      if (pins.has(convId)) pins.delete(convId);
      else pins.add(convId);
      const next = { ...prev, pinnedConversationIds: [...pins] };
      saveChatBlob(tenantKey, next);
      return next;
    });
  }, [tenantKey, userId]);

  const toggleStarMessage = useCallback(
    (messageId: string) => {
      setBlob((prev) => {
        const stars = new Set(prev.starredMessageIds);
        if (stars.has(messageId)) stars.delete(messageId);
        else stars.add(messageId);
        const next = { ...prev, starredMessageIds: [...stars] };
        saveChatBlob(tenantKey, next);
        return next;
      });
    },
    [tenantKey],
  );

  const toggleFavoriteConversation = useCallback(
    (peerId: string) => {
      const convId = dmConversationId(userId, peerId);
      setBlob((prev) => {
        const fav = new Set(prev.favoriteConversationIds || []);
        if (fav.has(convId)) fav.delete(convId);
        else fav.add(convId);
        const next = { ...prev, favoriteConversationIds: [...fav] };
        saveChatBlob(tenantKey, next);
        return next;
      });
    },
    [tenantKey, userId],
  );

  const dockFloatingChat = useCallback((peerId: string) => {
    setFloating((prev) => {
      if (!prev.some((p) => p.peerId === peerId)) return prev;
      return prev.map((p) =>
        p.peerId === peerId ? { ...p, presentation: 'docked', geom: { ...p.geom, minimized: false } } : p,
      );
    });
  }, []);

  const restoreDockedChat = useCallback(
    (peerId: string) => {
      markConversationRead(peerId);
      zCounter.current += 1;
      const z = zCounter.current;
      setFloating((prev) =>
        prev.map((p) =>
          p.peerId === peerId ? { ...p, presentation: 'floating', geom: { ...p.geom, zIndex: z, minimized: false } } : p,
        ),
      );
    },
    [markConversationRead],
  );

  const value: TeamChatContextValue = {
    tenantKey,
    teammates,
    refreshDirectory,
    blob,
    unreadTotal,
    floating,
    pageSelectedPeerId,
    setPageSelectedPeerId,
    openFloatingChat,
    closeFloatingChat,
    focusFloating,
    setWindowGeom,
    sendDm,
    editOwnDmMessage,
    softDeleteOwnDmMessage,
    canViewDeletionAudit,
    notifyTyping,
    typingPeerIds,
    markConversationRead,
    getMessages,
    togglePinConversation,
    toggleFavoriteConversation,
    toggleStarMessage,
    soundEnabled,
    setSoundEnabled,
    dockFloatingChat,
    restoreDockedChat,
  };

  return <TeamChatContext.Provider value={value}>{children}</TeamChatContext.Provider>;
}
